import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import { Delimiter } from "@/atoms-react/delimiter/Delimiter";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import InputLabel from "@/atoms-react/input/Input.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import RadioButton from "@/atoms-react/radio-button/index.react";
import Text from "@/atoms-react/text/Text.react.tsx";
import { Gender } from "@/features/auth/types";
import type { DeclarativePerson } from "@/features/flow/types";
import type { NaturalPerson } from "@/features/user/types.ts";
import { isDeclarativePersonValid } from "@/features/utils/declarative_identity_person";
import { FlowHeader } from "@/flow/components/flow-header.react";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";

interface DeclarativeIdentityOneProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeIdentityOneOutput) => void;
  declarativePerson?: DeclarativePerson;
  profile?: NaturalPerson | null;
}

export type DeclarativeIdentityOneOutput = DeclarativePerson;
function DeclarativeIdentityPerson({
  profile,
  declarativePerson,
  onValidated = () => {}
}: DeclarativeIdentityOneProps) {
  const { t } = useTranslation(["pass"]);
  const dispatch = useAppDispatch();
  const [data, setDeclarativeIdentityOne] = useState<DeclarativePerson>({
    gender: declarativePerson?.gender || (profile?.Gender as Gender | undefined),
    firstnames: declarativePerson?.firstnames || profile?.FirstName,
    lastnames: declarativePerson?.lastnames || profile?.LastName,
    otherfirstnames: declarativePerson?.otherfirstnames || profile?.OtherFirstNames,
    commonFirstName: declarativePerson?.commonFirstName || profile?.CommonFirstName,
    commonName: declarativePerson?.commonName || profile?.CommonName,
    nationalityCodes: declarativePerson?.nationalityCodes ?? []
  });

  function execute() {
    if (!isDeclarativePersonValid(data)) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated(data);
  }

  return (
    <>
      <Column className="justify-between p-4 xl:p-0">
        <div className="2xl:mb-12">
          <FlowHeader
            title={t("flow.id_control.signup.declarative_identity.label.one_title")}
            description={t("flow.id_control.signup.declarative_identity.label.one_description")}
            back={false}
            onPreviousClick={() => dispatch(setIsInPreviousStep(true))}
          />
          <Column>
            <RadioButton
              className={"mb-1"}
              choicesInRow={true}
              value={data.gender || ""}
              onChange={(output) => {
                const outputEnumKey: Gender = output as Gender;
                setDeclarativeIdentityOne((identity) => ({
                  ...identity,
                  gender: outputEnumKey
                }));
              }}
              label={` ${t("flow.id_control.input.gender_label")} *`}
              listOfChoices={[
                { id: Gender.Man, title: t("flow.id_control.input.gender_man") },
                { id: Gender.Woman, title: t("flow.id_control.input.gender_woman") }
              ]}
            />

            <Text type="info" className={"mb-2 font-semibold text-primary"}>
              {t("flow.id_control.input.names_helper")}
            </Text>

            <div className={"mt-2 mb-2 flex flex-col justify-between md:flex-row"}>
              <InputLabel
                autoComplete="given-name"
                placeholder={t("flow.id_control.input.firstname_placeholder")}
                className={"mb-1 md:mr-8 md:w-full"}
                value={data.firstnames ?? ""}
                onChange={(value: string) =>
                  setDeclarativeIdentityOne((identity) => ({ ...identity, firstnames: value }))
                }
                label={t("flow.id_control.input.firstname_label")}
                required={true}
              />
              <InputLabel
                autoComplete="family-name"
                placeholder={t("flow.id_control.input.lastname_placeholder")}
                className={"mb-1 md:w-full"}
                value={data.lastnames ?? ""}
                onChange={(value: string) =>
                  setDeclarativeIdentityOne((identity) => ({ ...identity, lastnames: value }))
                }
                label={t("flow.id_control.input.lastname_label")}
                required={true}
              />
            </div>
            <InputLabel
              autoComplete="given-name"
              required={false}
              placeholder={t("flow.id_control.input.others_firstname_placeholder")}
              className={"mb-1 md:mr-8 md:w-full"}
              value={data.otherfirstnames ?? ""}
              onChange={(value: string) =>
                setDeclarativeIdentityOne((identity) => ({ ...identity, otherfirstnames: value }))
              }
              label={t("flow.id_control.input.others_firstname_label")}
            />
          </Column>
        </div>
        <Delimiter />
        {/* common names */}
        <div className={"mb-4 flex flex-col justify-between md:flex-row"}>
          <InputLabel
            autoComplete="common-first-name"
            placeholder={t("flow.id_control.input.common_firstname_placeholder")}
            className={"mb-1 md:mr-8 md:w-full"}
            value={data.commonFirstName ?? ""}
            onChange={(value: string) =>
              setDeclarativeIdentityOne((identity) => ({ ...identity, commonFirstName: value }))
            }
            label={t("flow.id_control.input.common_firstname_label")}
            required={false}
          />
          <InputLabel
            autoComplete="common-name"
            placeholder={t("flow.id_control.input.common_name_placeholder")}
            className={"mb-1 md:w-full"}
            value={data.commonName ?? ""}
            onChange={(value: string) =>
              setDeclarativeIdentityOne((identity) => ({ ...identity, commonName: value }))
            }
            label={t("flow.id_control.input.common_name_label")}
            required={false}
          />
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={0} />
          <Button
            shortcut="enter"
            onClick={() => execute()}
            icon={<ChevronRightIcon />}
            className="my-4"
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </>
  );
}

export default DeclarativeIdentityPerson;
